import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { ArrowUpRight, Heart, MapPin, Star } from "lucide-react";
import { api, CATEGORY_LABELS } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function StartupCard({ startup, variant = "default" }) {
    const { user } = useAuth();
    const [following, setFollowing] = useState(!!startup.is_following);
    const [count, setCount] = useState(startup.follower_count || 0);
    const [imgIdx, setImgIdx] = useState(0);

    // Compose the card image set: prefer preview_images (max 3), then banner, then first gallery image; empty = monogram placeholder
    const preview = (startup.preview_images && startup.preview_images.length > 0)
        ? startup.preview_images.slice(0, 3)
        : (startup.banner_url ? [startup.banner_url]
            : (startup.gallery_images?.length ? [startup.gallery_images[0]] : []));

    // Auto-cycle preview images while card is visible if multiple
    const timer = useRef();
    useEffect(() => {
        if (preview.length <= 1) return;
        timer.current = setInterval(() => setImgIdx((i) => (i + 1) % preview.length), 3200);
        return () => clearInterval(timer.current);
    }, [preview.length]);

    const toggleFollow = async (e) => {
        e.preventDefault(); e.stopPropagation();
        if (!user) { toast.error("Log in to follow brands"); return; }
        try {
            const { data } = await api.post(`/startups/${startup.id}/follow`);
            setFollowing(data.following);
            setCount((c) => c + (data.following ? 1 : -1));
            toast.success(data.following ? `Following ${startup.brand_name}` : `Unfollowed ${startup.brand_name}`);
        } catch { toast.error("Try again"); }
    };

    const isArtisanal = startup.categories?.includes("artisanal");

    return (
        <Link
            to={`/startup/${startup.id}`}
            data-testid={`startup-card-${startup.id}`}
            className={`group relative overflow-hidden rounded-md border ${startup.featured ? "border-brand-marigold/60" : "border-white/10"} hover-lift neon-ring bg-brand-ink flex flex-col ${
                variant === "tall" ? "row-span-2 min-h-[500px]" : "min-h-[280px]"
            }`}
        >
            <div className="absolute inset-0">
                {preview.length > 0 ? preview.map((src, i) => (
                    <img key={i} src={src} alt={startup.brand_name}
                        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === imgIdx ? "opacity-70 group-hover:opacity-90" : "opacity-0"}`} />
                )) : (
                    <div className="absolute inset-0 bg-brand-ink flex items-center justify-center">
                        <span className="font-serif-editorial italic text-white/10 text-8xl leading-none select-none">
                            {startup.brand_name?.[0] || "P"}
                        </span>
                    </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
            </div>

            {/* Preview image counter dots */}
            {preview.length > 1 && (
                <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 flex gap-1.5">
                    {preview.map((_, i) => (
                        <span key={i} className={`h-1 rounded-full transition-all ${i === imgIdx ? "w-6 bg-brand-saffron" : "w-3 bg-white/40"}`} />
                    ))}
                </div>
            )}

            {startup.featured && (
                <div data-testid={`featured-badge-${startup.id}`} className="absolute top-3 left-3 z-20 flex items-center gap-1 px-2 py-1 bg-brand-marigold text-black rounded-sm font-mono-tag text-[10px] uppercase font-bold">
                    <Star className="w-3 h-3 fill-current" /> Editor's Pick
                </div>
            )}

            {isArtisanal && !startup.featured && (
                <div className="absolute top-3 left-3 z-20 px-2 py-1 bg-brand-madder text-white rounded-sm font-serif-editorial text-[11px] italic">
                    Handcrafted
                </div>
            )}

            <button
                data-testid={`follow-btn-${startup.id}`}
                onClick={toggleFollow}
                className={`absolute top-3 right-3 z-20 p-2 rounded-full backdrop-blur-md transition-colors ${
                    following ? "bg-brand-magenta text-white" : "bg-black/60 text-white hover:bg-brand-saffron hover:text-black"
                }`}
                aria-label="Follow brand"
            >
                <Heart className={`w-4 h-4 ${following ? "fill-current" : ""}`} />
            </button>

            <div className="relative z-10 mt-auto p-6 md:p-7">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                    {startup.categories?.slice(0, 2).map((c) => (
                        <span key={c} className={`font-mono-tag text-[10px] uppercase px-2 py-1 rounded-sm ${
                            c === "artisanal" ? "bg-brand-madder text-white" : "bg-brand-saffron text-black"
                        }`}>
                            {CATEGORY_LABELS[c] || c}
                        </span>
                    ))}
                    {startup.city && (
                        <span className="font-mono-tag text-[10px] uppercase px-2 py-1 border border-white/20 text-white/70 rounded-sm flex items-center gap-1">
                            <MapPin className="w-2.5 h-2.5" />{startup.city}
                        </span>
                    )}
                </div>
                <h3 className={`font-extrabold text-2xl md:text-3xl tracking-tighter text-white mb-1 ${isArtisanal ? "font-serif-editorial italic" : "font-display"}`}>
                    {startup.brand_name}
                </h3>
                <p className="text-white/70 text-sm mb-4 line-clamp-2">{startup.tagline}</p>
                <div className="flex items-center justify-between">
                    <span className="font-mono-tag text-xs text-brand-saffron flex items-center gap-1">
                        Visit <ArrowUpRight className="w-3 h-3" />
                    </span>
                    <span className="font-mono-tag text-xs text-white/60 flex items-center gap-1">
                        <Heart className="w-3 h-3" />{count}
                    </span>
                </div>
            </div>
        </Link>
    );
}
